/**
 * Resolution of admin-managed content over configuration defaults.
 *
 * `resolveSiteContent` is the only place the two sources are combined. Every
 * component reads the resolved object, so a value is never sometimes-a-
 * placeholder and sometimes-CMS depending on the call site.
 *
 * Resolution rules:
 *  - A published CMS value wins.
 *  - An empty string or a value that is itself a placeholder does not win.
 *  - Anything still unresolved keeps its visible placeholder token.
 */

import type { PathwayId } from './brand.js';
import { brand, pathways } from './brand.js';
import { coach } from './coach.js';
import { contact, socialLinks, businessHours, locations } from './contact.js';
import { legal } from './legal.js';
import { assetUrl, brandAssets, coachAssets, posterAssets } from './assets.js';
import { resolve, isPlaceholder, listUnresolvedPlaceholders } from './placeholders.js';
import type { UnresolvedPlaceholder } from './placeholders.js';
import type { HeroContent, SiteContent } from '@ircf/contracts';

export interface ResolvedHero {
  headline: string;
  supporting: string;
  primaryCta: { label: string; path: string };
  secondaryCta: { label: string; path: string };
  posterUrl: string;
}

export interface ResolvedContent {
  business: {
    name: string;
    logoUrl: string;
    monogramUrl: string;
    monogramText: string;
    email: string;
    phone: string;
    businessHoursSummary: string;
    operatingHours: typeof businessHours;
    socialLinks: { id: string; label: string; url: string; icon: string }[];
    locations: typeof locations;
  };
  coach: {
    name: string;
    biography: string;
    philosophy: string[];
    commitments: readonly string[];
    qualifications: string;
    experience: string;
    boxingHistory: string;
    personalTrainingHistory: string;
    nutritionScope: string;
    credentials: { id: string; label: string; value: string; assetKey: string; resolved: boolean }[];
    heroImageUrl: string;
    portraitImageUrl: string;
    fullBodyImageUrl: string;
    personalTrainingImageUrl: string;
    boxingImageUrl: string;
    introductionVideoUrl: string | null;
    introductionVideoTranscript: string | null;
  };
  heroes: Record<PathwayId, ResolvedHero>;
  legal: {
    privacyPolicy: string;
    termsAndConditions: string;
    trainingDisclaimer: string;
    nutritionDisclaimer: string;
    cancellationPolicy: string;
    refundPolicy: string;
    accessibilityStatement: string;
    documentVersion: string;
    standingTrainingDisclaimer: string;
    standingNutritionDisclaimer: string;
    complianceNotice: string;
  };
  /** Free-form copy slots, CMS value falling back to the built-in default. */
  copy: (slotId: string, fallback: string) => string;
  /** Only present when genuinely collected. Never fabricated. */
  reviewSummary: SiteContent['reviewSummary'] | null;
  statistics: SiteContent['statistics'] | null;
  /** Whether the CMS has ever been published, for the admin readiness banner. */
  hasPublishedContent: boolean;
}

const DEFAULT_HEROES: Record<PathwayId, ResolvedHero> = {
  master: {
    headline: pathways.master.tagline,
    supporting: pathways.master.supporting,
    primaryCta: { label: 'Find Your Path', path: '/programs' },
    secondaryCta: { label: 'Book a Consultation', path: '/book' },
    posterUrl: posterAssets.masterHero,
  },
  'personal-training': {
    headline: 'Nutrition. Strength. Conditioning. Built Around You.',
    supporting: pathways['personal-training'].supporting,
    primaryCta: { label: 'Build Your Plan', path: '/book' },
    secondaryCta: { label: 'Explore Coaching Options', path: '/programs' },
    posterUrl: posterAssets.personalTraining,
  },
  boxing: {
    headline: 'Boxing. Strength. Conditioning. Built Around You.',
    supporting: pathways.boxing.supporting,
    primaryCta: { label: 'Book Your First Round', path: '/book' },
    secondaryCta: { label: 'Explore Coaching Programs', path: '/programs' },
    posterUrl: posterAssets.boxing,
  },
};

function resolveHero(fallback: ResolvedHero, override: HeroContent | undefined): ResolvedHero {
  if (!override) return fallback;
  return {
    headline: resolve(override.headline, fallback.headline),
    supporting: resolve(override.supporting, fallback.supporting),
    primaryCta: {
      label: resolve(override.primaryCtaLabel, fallback.primaryCta.label),
      path: resolve(override.primaryCtaPath, fallback.primaryCta.path),
    },
    secondaryCta: {
      label: resolve(override.secondaryCtaLabel, fallback.secondaryCta.label),
      path: resolve(override.secondaryCtaPath, fallback.secondaryCta.path),
    },
    posterUrl: resolve(override.posterUrl, fallback.posterUrl),
  };
}

/**
 * A monogram that is always safe to render inside a small square badge.
 *
 * Unlike body copy, a placeholder token cannot be displayed here - `[COACH
 * MONOGRAM]` in a 40px box is unreadable and looks broken rather than
 * intentionally unfinished. So this degrades: supplied monogram, then initials
 * derived from the coach name, then the master brand initials.
 */
function resolveMonogram(supplied: string | undefined, coachName: string): string {
  if (supplied && !isPlaceholder(supplied) && supplied.trim().length > 0) {
    return supplied.trim().slice(0, 3).toUpperCase();
  }

  if (!isPlaceholder(coachName)) {
    const initials = coachName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
    if (initials.length > 0) return initials;
  }

  return 'BWP';
}

/** Joins a string array into a paragraph, or returns the placeholder unchanged. */
function firstNonEmpty(...candidates: (string | undefined | null)[]): string | null {
  for (const candidate of candidates) {
    if (candidate && candidate.trim().length > 0 && !isPlaceholder(candidate)) {
      return candidate.trim();
    }
  }
  return null;
}

export function resolveSiteContent(published: SiteContent | null | undefined): ResolvedContent {
  const cms = published ?? {};
  const business = cms.business ?? {};
  const cmsCoach = cms.coach ?? {};
  const cmsLegal = cms.legal ?? {};
  const copyBlocks = cms.copyBlocks ?? {};

  // Social links whose URL is still a placeholder are removed rather than
  // rendered as a dead link.
  const resolvedSocials = (business.socialLinks ?? socialLinks).filter(
    (link) => !isPlaceholder(link.url) && link.url.trim().length > 0,
  );

  return {
    business: {
      name: resolve(business.businessName, brand.businessName),
      logoUrl: assetUrl(resolve(business.logoUrl, brandAssets.logoPrimary)),
      monogramUrl: assetUrl(resolve(business.monogramUrl, brandAssets.monogram)),
      monogramText: resolveMonogram(
        cmsCoach.monogram ?? brand.monogram,
        resolve(cmsCoach.name, coach.name),
      ),
      email: resolve(business.email, contact.email),
      phone: resolve(business.phone, contact.phone),
      businessHoursSummary: resolve(business.businessHoursSummary, contact.businessHoursSummary),
      operatingHours: businessHours,
      socialLinks: resolvedSocials.map((link) => ({ ...link, icon: String(link.icon) })),
      locations,
    },

    coach: {
      name: resolve(cmsCoach.name, coach.name),
      biography: resolve(cmsCoach.biography, coach.biography),
      philosophy:
        cmsCoach.philosophy && cmsCoach.philosophy.length > 0
          ? cmsCoach.philosophy
          : [...coach.philosophy],
      commitments: coach.commitments,
      qualifications: resolve(cmsCoach.qualifications?.join('\n'), coach.qualifications),
      experience: resolve(cmsCoach.yearsCoaching, coach.experience),
      boxingHistory: resolve(cmsCoach.boxingHistory, coach.boxingHistory),
      personalTrainingHistory: resolve(
        cmsCoach.personalTrainingHistory,
        coach.personalTrainingHistory,
      ),
      nutritionScope: resolve(cmsCoach.nutritionScope, coach.nutritionScope),
      credentials: coach.credentials.map((credential) => ({
        id: credential.id,
        label: credential.label,
        value: credential.value,
        assetKey: credential.assetKey,
        resolved: !isPlaceholder(credential.value),
      })),
      heroImageUrl: assetUrl(resolve(cmsCoach.heroImageUrl, coachAssets.hero)),
      portraitImageUrl: assetUrl(resolve(cmsCoach.portraitImageUrl, coachAssets.portrait)),
      fullBodyImageUrl: assetUrl(
        resolve(cmsCoach.fullBodyImageUrl, coachAssets.fullBodyTransparent),
      ),
      personalTrainingImageUrl: assetUrl(
        resolve(cmsCoach.personalTrainingImageUrl, coachAssets.personalTraining),
      ),
      boxingImageUrl: assetUrl(resolve(cmsCoach.boxingImageUrl, coachAssets.boxing)),
      introductionVideoUrl: firstNonEmpty(cmsCoach.introductionVideoUrl),
      introductionVideoTranscript: firstNonEmpty(cmsCoach.introductionVideoTranscript),
    },

    heroes: {
      master: resolveHero(DEFAULT_HEROES.master, cms.heroes?.master),
      'personal-training': resolveHero(
        DEFAULT_HEROES['personal-training'],
        cms.heroes?.personalTraining,
      ),
      boxing: resolveHero(DEFAULT_HEROES.boxing, cms.heroes?.boxing),
    },

    legal: {
      privacyPolicy: resolve(cmsLegal.privacyPolicy, legal.privacyPolicy),
      termsAndConditions: resolve(cmsLegal.termsAndConditions, legal.termsAndConditions),
      trainingDisclaimer: resolve(cmsLegal.trainingDisclaimer, legal.trainingDisclaimer),
      nutritionDisclaimer: resolve(cmsLegal.nutritionDisclaimer, legal.nutritionDisclaimer),
      cancellationPolicy: resolve(cmsLegal.cancellationPolicy, legal.cancellationPolicy),
      refundPolicy: resolve(cmsLegal.refundPolicy, legal.refundPolicy),
      accessibilityStatement: resolve(
        cmsLegal.accessibilityStatement,
        legal.accessibilityStatement,
      ),
      documentVersion: resolve(cmsLegal.documentVersion, legal.documentVersion),
      standingTrainingDisclaimer: legal.standingTrainingDisclaimer,
      standingNutritionDisclaimer: legal.standingNutritionDisclaimer,
      complianceNotice: legal.complianceNotice,
    },

    copy: (slotId: string, fallback: string) => resolve(copyBlocks[slotId], fallback),

    reviewSummary: cms.reviewSummary ?? null,
    statistics: cms.statistics ?? null,
    hasPublishedContent: Boolean(cms.publishedAt),
  };
}

/**
 * Enumerates every unresolved placeholder across the whole configuration tree.
 * Powers the admin content-readiness panel and is asserted by a unit test
 * against docs/CONTENT_REQUIRED.md.
 */
export function contentReadiness(): {
  unresolved: UnresolvedPlaceholder[];
  tokens: string[];
} {
  const unresolved = listUnresolvedPlaceholders({
    brand: { businessName: brand.businessName, logoPath: brand.logoPath, monogram: brand.monogram },
    coach,
    contact,
    legal: {
      privacyPolicy: legal.privacyPolicy,
      termsAndConditions: legal.termsAndConditions,
      trainingDisclaimer: legal.trainingDisclaimer,
      nutritionDisclaimer: legal.nutritionDisclaimer,
      cancellationPolicy: legal.cancellationPolicy,
      refundPolicy: legal.refundPolicy,
      healthScreeningIntro: legal.healthScreeningIntro,
      parQContent: legal.parQContent,
    },
  });

  const tokens = [...new Set(unresolved.map((entry) => entry.token))].sort();
  return { unresolved, tokens };
}
